/* PALISADE CUSTOM — cart selling-plan switching (drawer and cart page), both directions.
 *
 *   one-time      → subscription   the "Upgrade to a Subscription" pill
 *   subscription  → any frequency  the plan select (snippets/palisade-cart-plan)
 *   subscription  → one-time       the same select's "One-time" option
 *
 * The last two did not exist before: a shopper who picked a subscription had no
 * way back to a one-time purchase from the cart.
 *
 * Delegated document-level listener so it survives the drawer's Section
 * Rendering re-renders. On click: POST /cart/change.js with the line's Skio
 * selling plan, then re-render the cart-items section holding the control in
 * place (the dialog stays open; Horizon's declarative on:click handlers keep
 * working because they resolve at event time).
 *
 * Accessibility contract (WCAG 2.4.3 / 4.1.3): the pill is guarded with
 * aria-disabled (not `disabled`, which would eject focus mid-request); after
 * the swap, focus moves to the drawer heading and the change is announced via
 * a polite live region; failures surface in the row's error container.
 */
import { sectionRenderer } from '@theme/section-renderer';
import { CartLinesUpdateEvent } from '@shopify/events';

/* Which input modality drove the last interaction, so the post-swap focus can be
 * silent for a mouse and visible for a keyboard. Both listeners are capturing and
 * passive: they only observe, and must see the event even if a component below
 * stops it propagating.
 *
 * Tab is not special-cased — any keydown counts. A shopper arrowing through the
 * select's options is using the keyboard just as much as one who tabbed to it, and
 * both should get the ring back on the control that replaces it.
 */
let keyboardModality = false;
document.addEventListener('keydown', () => { keyboardModality = true; }, { capture: true, passive: true });
document.addEventListener('pointerdown', () => { keyboardModality = false; }, { capture: true, passive: true });

/* PALISADE CUSTOM — a POINTER press on the frequency selector marks it quiet.
 *
 * Chrome matches :focus-visible on a <select> that is clicked, not only one that
 * is tabbed to, because clicking it opens a listbox. So the plum ring appeared
 * around the pill every time a shopper opened it with the mouse — read as a
 * doubled border against the pill's own plum hairline.
 *
 * Marked here rather than suppressed in CSS so KEYBOARD focus keeps its ring,
 * which WCAG 2.4.7 requires. The keydown handler below clears the marker, so
 * tabbing to the same control still shows it.
 */
document.addEventListener(
  'pointerdown',
  (event) => {
    const select = event.target?.closest?.('.palisade-cart-plan__select');
    if (select) select.setAttribute('data-pc-quiet-focus', '');
  },
  { capture: true, passive: true }
);

/* The quiet-focus marker is cleared the moment the shopper touches a key, so the
 * ring returns on the very next keyboard interaction rather than staying suppressed
 * until the element is replaced again. */
document.addEventListener(
  'keydown',
  (event) => {
    const quiet = event.target?.closest?.('[data-pc-quiet-focus]');
    if (quiet) quiet.removeAttribute('data-pc-quiet-focus');
  },
  { capture: true }
);

function announce(message) {
  let region = document.getElementById('palisade-cart-upsell-status');
  if (!region) {
    region = document.createElement('div');
    region.id = 'palisade-cart-upsell-status';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.className = 'visually-hidden';
    // Outside .cart-drawer__inner so the section swap never destroys it.
    const dialog = document.querySelector('#cart-drawer dialog') || document.body;
    dialog.appendChild(region);
  }
  region.textContent = '';
  // Re-set on a tick so repeat messages are re-announced.
  requestAnimationFrame(() => {
    region.textContent = message;
  });
}

function restoreRenderedOption(select) {
  for (const option of select.options) option.selected = option.defaultSelected;
}

function showRowError(control, line) {
  // The failed write left the cart unchanged, so the select must not keep showing the option that failed.
  if (control instanceof HTMLSelectElement) restoreRenderedOption(control);
  const container = document.querySelector(`[ref="cartItemErrorContainer-${line}"]`);
  const text = document.querySelector(`[ref="cartItemError-${line}"]`);
  if (container && text) {
    text.textContent = control.dataset.errorMessage || 'Could not update this item. Please try again.';
    container.classList.remove('hidden');
  }
}

/* Change a line's selling plan and re-render the cart.
 *
 * `sellingPlan` is passed through to the cart API VERBATIM and must be either a
 * plan id or the empty string. Do not coerce it with Number(): '' becomes 0, and
 * 0 is rejected with 422 "Cannot apply selling plan to variant" — verified
 * against this store. '' (and null) are the forms that clear a plan.
 *
 * Shared by the upgrade pill and the plan select so the two directions cannot
 * drift apart: same guard, same error surface, same swap, same focus contract.
 */
async function applyPlanChange(control, sellingPlan, successMessage) {
  const line = Number(control.dataset.line);
  // Quantity read at EVENT time from the row's input — the render-time
  // data-quantity snapshot can be stale after a quantity change in flight.
  const row = control.closest('tr') || control.closest('.cart-items__table-row');
  const quantityInput = row && row.querySelector('input[name="quantity"]');
  const quantity = Number(
    (quantityInput && quantityInput.value) || control.dataset.quantity || 1
  );

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity, selling_plan: sellingPlan }),
    });
    if (!response.ok) throw new Error(`cart/change ${response.status}`);
  } catch (error) {
    console.warn('[palisade-cart-upsell]', error);
    showRowError(control, line);
    announce(control.dataset.errorMessage || 'Could not update this item.');
    control.removeAttribute('aria-disabled');
    return;
  }

  await refreshCart(control, { line }, successMessage);
}

/* Re-render the cart and land focus deliberately.
 *
 * Extracted so the plan change and the product swap share ONE implementation:
 * they have identical post-write obligations (never report failure once the cart
 * has changed, fall back to a reload if the section render is unavailable, move
 * focus because the operated control can be replaced by the re-render), and two
 * copies of that contract would drift.
 *
 * Re-renders the cart-items section that holds the control (drawer or cart
 * page), then announces the change with the same CartLinesUpdateEvent Horizon's
 * quantity change sends, so the header count and any other cart surface follow.
 *
 * `target.key` locates a swapped line: the add puts the new line at the TOP of
 * the cart, so its old index belongs to another line afterwards.
 */
async function refreshCart(control, target, successMessage) {
  // The cart change SUCCEEDED — from here on, a failed re-render must never
  // report failure (the line already changed); fall back to a hard refresh so
  // the visible cart matches reality.
  const { line, key } = target;
  const surface = control.closest('cart-items-component');
  const surfaceId = surface?.dataset.sectionId;
  const root = window.Shopify?.routes?.root || '/';
  try {
    if (!surfaceId) throw new Error('no cart section to re-render');
    const render = () =>
      sectionRenderer.renderSection(surfaceId, {
        cache: false,
        mode: surface.hasAttribute('data-drawer') ? 'hydration' : 'full',
      });
    const cartRequest = fetch(`${root}cart.js`).then((response) => (response.ok ? response.json() : null), () => null);
    // An empty result means a concurrent Horizon update aborted this render, and that update may not morph.
    if (!(await render()) && !(await render())) throw new Error(`section ${surfaceId} render superseded`);
    const scope = document.getElementById(`shopify-section-${surfaceId}`);
    if (!scope) throw new Error(`section ${surfaceId} missing after re-render`);

    const cart = await cartRequest;
    const changed = cart && ((key && cart.items.find((item) => item.key === key)) || cart.items[line - 1]);
    if (cart) {
      // Dispatched from the re-rendered component itself, which skips its own events, so it is not rendered twice.
      (scope.querySelector('cart-items-component') || document).dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: [{ id: changed?.key ?? '', quantity: changed?.quantity ?? 0 }],
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, itemCount: cart.item_count, source: 'palisade-cart-upsell', didError: false },
          }),
        })
      );
    }

    /* Focus has to land somewhere deliberate (WCAG 2.4.3). Prefer whichever
       control now occupies that line — the select stays a select when only the
       frequency changed, and becomes the upgrade pill when the line went back to
       one-time — so a shopper can keep adjusting without hunting for it. The
       heading is the fallback for a line that no longer exists. */
    const row = key ? scope.querySelector(`[data-key="${CSS.escape(key)}"]`) : null;
    const successor =
      (key
        ? row?.querySelector('[data-pc-plan], [data-pc-upsell]')
        : scope.querySelector(`[data-pc-plan][data-line="${line}"], [data-pc-upsell][data-line="${line}"]`)) ||
      scope.querySelector('#cart-drawer-heading, .cart-title h1');
    if (successor) {
      if (!successor.matches('[data-pc-plan], [data-pc-upsell]')) successor.setAttribute('tabindex', '-1');
      /* Focus still MOVES for everyone — the drawer is a modal dialog, so
         letting focus fall to <body> would drop it outside the dialog's focus
         scope. What is suppressed is the visible RING, and only for pointer
         users: `:focus-visible` matches on programmatic focus for these
         controls, so a mouse-driven change was drawing a plum ring 2px outside
         the pill's own plum border, which read as a doubled border rather than
         as focus. Keyboard users keep the ring — removing it outright would
         break WCAG 2.4.7. */
      if (!keyboardModality) successor.setAttribute('data-pc-quiet-focus', '');
      successor.focus();
    }
    announce(successMessage);
  } catch (error) {
    console.warn('[palisade-cart-upsell]', error);
    window.location.reload();
  }
}

/* Swap this line for a DIFFERENT product's plan (1-pack <-> 3-pack).
 *
 * A selling plan carries no quantity and /cart/change.js carries no variant, so
 * moving between the 1-month plan and the 3-pack is two writes, not one. They are
 * ordered ADD FIRST, then remove:
 *
 *   add fails    -> nothing was destroyed; the shopper still has their line.
 *   remove fails -> the shopper has both lines, which is visible and fixable,
 *                   rather than an empty cart they have to rebuild.
 *
 * The reverse order is shorter but its failure mode is losing the line entirely.
 *
 * The old line is removed by its `key`, not its index: an add can renumber lines,
 * and a stale index would remove somebody else's row.
 */
async function applyProductSwap(control, variantId, sellingPlan, successMessage) {
  const line = Number(control.dataset.line);
  const row = control.closest('tr') || control.closest('.cart-items__table-row');
  const key = row && row.dataset.key;
  const root = window.Shopify?.routes?.root || '/';

  if (!key) {
    console.warn('[palisade-cart-upsell] no line key; refusing a swap that could remove the wrong row');
    showRowError(control, line);
    control.removeAttribute('aria-disabled');
    return;
  }

  let added;
  try {
    added = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          sellingPlan === ''
            ? { id: variantId, quantity: 1 }
            : { id: variantId, quantity: 1, selling_plan: sellingPlan },
        ],
      }),
    });
    if (!added.ok) throw new Error(`cart/add ${added.status}`);
  } catch (error) {
    console.warn('[palisade-cart-upsell]', error);
    showRowError(control, line);
    announce(control.dataset.errorMessage || 'Could not update this item.');
    control.removeAttribute('aria-disabled');
    return;
  }

  /* The add SUCCEEDED. From here the cart has already changed, so nothing below
     may report failure to the shopper — a failed remove leaves a duplicate line,
     which the re-render shows them, and a failed re-render falls back to reload. */
  const addedKey = await added.json().then((json) => json.items?.[0]?.key, () => undefined);
  try {
    await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
  } catch (error) {
    console.warn('[palisade-cart-upsell] swap added but old line remains', error);
  }

  await refreshCart(control, { line, key: addedKey }, successMessage);
}

/* Horizon's own cart lock for the length of the write, so a remove or stepper
 * click cannot post a line index this change is about to shift. */
async function withCartLock(control, operation) {
  const surface = control.closest('cart-items-component');
  surface?.classList.add('cart-items-disabled');
  try {
    await operation();
  } finally {
    surface?.classList.remove('cart-items-disabled');
  }
}

/* One-time → subscription (the upgrade pill). */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-pc-upsell]');
  if (!button || button.getAttribute('aria-disabled') === 'true') return;
  // Skip while Horizon has the cart locked mid-update (stale line indices).
  if (button.closest('.cart-items-disabled')) return;
  event.preventDefault();

  button.setAttribute('aria-disabled', 'true');

  /* The featured offer may live on another product (the 3-pack), in which case
     the pill is a product swap, not a plan change — same split as the select. */
  const upsellVariant = button.dataset.pcVariant;
  const message = button.dataset.successMessage || 'Item upgraded to a subscription.';
  await withCartLock(button, () =>
    upsellVariant
      ? applyProductSwap(button, Number(upsellVariant), Number(button.dataset.sellingPlan), message)
      : applyPlanChange(button, Number(button.dataset.sellingPlan), message)
  );
});

/* Subscription → another frequency, or back to one-time (the plan select).
 *
 * `change`, not `input`: a native select fires change once the shopper has
 * committed, whereas `input` also fires while arrow-keying through the options —
 * which would post a cart change per option passed over.
 */
document.addEventListener('change', async (event) => {
  const select = event.target.closest('[data-pc-plan]');
  if (!select) return;
  if (select.getAttribute('aria-disabled') === 'true' || select.closest('.cart-items-disabled')) {
    // Arrow keys on a closed select still fire change mid-request; snap back rather than show a choice never sent.
    if (select.dataset.pcPending !== undefined) select.value = select.dataset.pcPending;
    else restoreRenderedOption(select);
    return;
  }

  const value = select.value;
  select.setAttribute('aria-disabled', 'true');
  select.dataset.pcPending = value;

  /* An option carrying data-pc-variant targets ANOTHER product (1-pack <-> 3-pack),
     which /cart/change.js cannot express — it takes line, quantity and selling_plan
     only, never a variant. Those go through the add-then-remove path instead. */
  const chosen = select.options[select.selectedIndex];
  const targetVariant = chosen && chosen.dataset.pcVariant;
  const message =
    value === ''
      ? select.dataset.oneTimeMessage || 'Switched to a one-time purchase.'
      : select.dataset.planMessage || 'Delivery frequency updated.';
  try {
    await withCartLock(select, () =>
      targetVariant
        ? applyProductSwap(
            select,
            Number(targetVariant),
            /* '' stays '' — Number('') is 0, and 0 is the 422 documented on
               applyPlanChange. An empty value here means "switch product AND drop the
               subscription", which the add below expresses by omitting the key. */
            value === '' ? '' : Number(value),
            message
          )
        : applyPlanChange(select, value === '' ? '' : Number(value), message)
    );
  } finally {
    delete select.dataset.pcPending;
  }
});
